import { create } from 'zustand';

import { wallsApi } from '@walls/services/wallsApi';
import {
  createClusterFromCardsInTree,
  deleteClusterAndPromoteChildrenInTree,
  type DeleteClusterPromotionResult,
  detachCardFromClusterInTree,
  moveCardToClusterInTree,
  sortClusterChildrenInTree,
} from './clusterTree';
import type { WallCard, WallConnection, WallId, WallShape } from './types';

interface WallContentStoreState {
  cards: WallCard[];
  connections: WallConnection[];
  shapes: WallShape[];
  boardContentLoaded: boolean;
  isLoadingBoardContent: boolean;
  boardContentLoadError: string | null;
}

interface LoadBoardContentOptions {
  topicKey?: string | null;
}

interface WallContentStoreActions {
  addCard: (card: WallCard) => void;
  addCards: (cards: WallCard[]) => void;
  addConnection: (connection: WallConnection) => void;
  addShape: (shape: WallShape) => void;
  loadBoardContent: (boardId: WallId, options?: LoadBoardContentOptions) => Promise<void>;
  replaceCardsAndConnections: (cards: WallCard[], connections: WallConnection[]) => void;
  removeCard: (cardId: string) => void;
  removeConnection: (connectionId: string) => void;
  removeConnectionsByCardId: (cardId: string) => void;
  removeConnectionsByParticipant: (participantId: string) => void;
  removeCardsByParticipant: (participantId: string) => void;
  removeShape: (shapeId: string) => void;
  resetBoardContent: () => void;
  upsertCard: (card: WallCard) => void;
  upsertConnection: (connection: WallConnection) => void;
  upsertShape: (shape: WallShape) => void;
  createClusterFromCards: (sourceCardId: string, targetCardId: string) => void;
  deleteClusterAndPromoteChildren: (clusterId: string) => DeleteClusterPromotionResult;
  detachCardFromCluster: (cardId: string, xPos: number, yPos: number) => void;
  moveCardToCluster: (cardId: string, clusterId: string, zPos: number) => void;
  sortClusterChildren: (clusterId: string, orderedIds: string[]) => void;
  updateCard: (cardId: string, patch: Partial<WallCard>) => void;
  updateCards: (cardIds: string[], patch: Partial<WallCard>) => void;
  updateConnection: (connectionId: string, patch: Partial<WallConnection>) => void;
  updateShape: (shapeId: string, patch: Partial<WallShape>) => void;
  updateCardZIndexes: (zIndexes: Array<{ cardId: string; zPos: number }>) => void;
  updateShapeZIndexes: (zIndexes: Array<{ shapeId: string; zPos: number }>) => void;
}

export type WallContentStore = WallContentStoreState & WallContentStoreActions;

const emptyContent = {
  cards: [],
  connections: [],
  shapes: [],
};

export const useWallContentStore = create<WallContentStore>((set, get) => ({
  cards: [],
  connections: [],
  shapes: [],
  boardContentLoaded: false,
  isLoadingBoardContent: false,
  boardContentLoadError: null,

  addCard: (card) => set((state) => ({ cards: [...state.cards, normalizeCard(card)] })),

  addCards: (cards) =>
    set((state) => ({ cards: [...state.cards, ...cards.map((card) => normalizeCard(card))] })),

  addConnection: (connection) =>
    set((state) => ({ connections: [...state.connections, normalizeConnection(connection)] })),

  addShape: (shape) => set((state) => ({ shapes: [...state.shapes, normalizeShape(shape)] })),

  loadBoardContent: async (boardId, options) => {
    set({
      boardContentLoaded: true,
      isLoadingBoardContent: true,
      boardContentLoadError: null,
    });

    const cardsAndConnections = wallsApi
      .getCards(boardId, options?.topicKey)
      .then(async (cards) => {
        set({ cards });

        const connections = await wallsApi.getConnections(boardId, options?.topicKey);
        set({ connections });
      })
      .catch((error) => {
        set({
          cards: [],
          connections: [],
          boardContentLoadError:
            error instanceof Error ? error.message : 'Error retrieving board cards',
        });
      });

    const shapes = wallsApi
      .getShapes(boardId, options?.topicKey)
      .then((loadedShapes) => {
        set({ shapes: loadedShapes });
      })
      .catch((error) => {
        set({
          shapes: [],
          boardContentLoadError:
            error instanceof Error ? error.message : 'Error retrieving board shapes',
        });
      });

    await Promise.allSettled([cardsAndConnections, shapes]);

    set({ isLoadingBoardContent: false });
  },

  replaceCardsAndConnections: (cards, connections) =>
    set({
      cards: cards.map((card) => normalizeCardTree(card)),
      connections: connections.map((connection) => normalizeConnection(connection)),
    }),

  removeCard: (cardId) => set((state) => ({ cards: removeCardFromTree(state.cards, cardId) })),

  removeConnection: (connectionId) =>
    set((state) => ({
      connections: state.connections.filter((connection) => connection.id !== connectionId),
    })),

  removeConnectionsByCardId: (cardId) =>
    set((state) => ({
      connections: state.connections.filter(
        (connection) => connection.start?.cardId !== cardId && connection.end?.cardId !== cardId
      ),
    })),

  removeConnectionsByParticipant: (participantId) =>
    set((state) => ({
      connections: state.connections.filter((connection) => connection.ownerId !== participantId),
    })),

  removeCardsByParticipant: (participantId) =>
    set((state) => ({
      cards: removeCardsByParticipantFromTree(state.cards, participantId),
    })),

  removeShape: (shapeId) =>
    set((state) => ({ shapes: state.shapes.filter((shape) => shape.id !== shapeId) })),

  resetBoardContent: () =>
    set({
      ...emptyContent,
      boardContentLoaded: false,
      isLoadingBoardContent: false,
      boardContentLoadError: null,
    }),

  upsertCard: (card) =>
    set((state) => ({
      cards: cardExistsInTree(state.cards, card.id)
        ? updateCardInTree(state.cards, card.id, card)
        : [...state.cards, normalizeCard(card)],
    })),

  upsertConnection: (connection) =>
    set((state) => ({
      connections: state.connections.some((candidate) => candidate.id === connection.id)
        ? state.connections.map((candidate) =>
            candidate.id === connection.id ? normalizeConnection(connection) : candidate
          )
        : [...state.connections, normalizeConnection(connection)],
    })),

  upsertShape: (shape) =>
    set((state) => ({
      shapes: state.shapes.some((candidate) => candidate.id === shape.id)
        ? state.shapes.map((candidate) =>
            candidate.id === shape.id ? normalizeShape(shape) : candidate
          )
        : [...state.shapes, normalizeShape(shape)],
    })),

  createClusterFromCards: (sourceCardId, targetCardId) =>
    set((state) => ({
      cards: createClusterFromCardsInTree(state.cards, sourceCardId, targetCardId),
    })),

  deleteClusterAndPromoteChildren: (clusterId) => {
    const result = deleteClusterAndPromoteChildrenInTree(get().cards, clusterId);
    set({ cards: result.cards });
    return result;
  },

  detachCardFromCluster: (cardId, xPos, yPos) =>
    set((state) => ({ cards: detachCardFromClusterInTree(state.cards, cardId, xPos, yPos) })),

  moveCardToCluster: (cardId, clusterId, zPos) =>
    set((state) => ({ cards: moveCardToClusterInTree(state.cards, cardId, clusterId, zPos) })),

  sortClusterChildren: (clusterId, orderedIds) =>
    set((state) => ({ cards: sortClusterChildrenInTree(state.cards, clusterId, orderedIds) })),

  updateCard: (cardId, patch) =>
    set((state) => ({ cards: updateCardInTree(state.cards, cardId, patch) })),

  updateCards: (cardIds, patch) =>
    set((state) => ({ cards: updateCardsInTree(state.cards, cardIds, patch) })),

  updateConnection: (connectionId, patch) =>
    set((state) => ({
      connections: state.connections.map((connection) =>
        connection.id === connectionId
          ? normalizeConnection({ ...connection, ...patch })
          : connection
      ),
    })),

  updateShape: (shapeId, patch) =>
    set((state) => ({
      shapes: state.shapes.map((shape) =>
        shape.id === shapeId ? normalizeShape({ ...shape, ...patch }) : shape
      ),
    })),

  updateCardZIndexes: (zIndexes) =>
    set((state) => ({ cards: updateCardZIndexesInTree(state.cards, zIndexes) })),

  updateShapeZIndexes: (zIndexes) =>
    set((state) => ({
      shapes: state.shapes.map((shape) => {
        const zIndex = zIndexes.find((currentZIndex) => currentZIndex.shapeId === shape.id);

        return zIndex ? { ...shape, zPos: zIndex.zPos } : shape;
      }),
    })),
}));

function updateCardInTree(cards: WallCard[], cardId: string, patch: Partial<WallCard>): WallCard[] {
  return cards.map((card) => {
    if (card.id === cardId) {
      return normalizeCard({ ...card, ...patch });
    }

    if (card.cards.length > 0) {
      return { ...card, cards: updateCardInTree(card.cards, cardId, patch) };
    }

    return card;
  });
}

function cardExistsInTree(cards: WallCard[], cardId: string): boolean {
  return cards.some((card) => card.id === cardId || cardExistsInTree(card.cards, cardId));
}

function updateCardsInTree(
  cards: WallCard[],
  cardIds: string[],
  patch: Partial<WallCard>
): WallCard[] {
  return cards.map((card) => {
    const updatedCard = cardIds.includes(card.id) ? normalizeCard({ ...card, ...patch }) : card;

    if (updatedCard.cards.length > 0) {
      return { ...updatedCard, cards: updateCardsInTree(updatedCard.cards, cardIds, patch) };
    }

    return updatedCard;
  });
}

function updateCardZIndexesInTree(
  cards: WallCard[],
  zIndexes: Array<{ cardId: string; zPos: number }>
): WallCard[] {
  return cards.map((card) => {
    const zIndex = zIndexes.find((currentZIndex) => currentZIndex.cardId === card.id);
    const updatedCard = zIndex ? normalizeCard({ ...card, zPos: zIndex.zPos }) : card;

    if (updatedCard.cards.length > 0) {
      return {
        ...updatedCard,
        cards: updateCardZIndexesInTree(updatedCard.cards, zIndexes),
      };
    }

    return updatedCard;
  });
}

function removeCardFromTree(cards: WallCard[], cardId: string): WallCard[] {
  return cards
    .filter((card) => card.id !== cardId)
    .map((card) => ({ ...card, cards: removeCardFromTree(card.cards, cardId) }));
}

function removeCardsByParticipantFromTree(cards: WallCard[], participantId: string): WallCard[] {
  return cards
    .filter((card) => card.ownerId !== participantId)
    .map((card) => ({
      ...card,
      cards: removeCardsByParticipantFromTree(card.cards, participantId),
    }));
}

function normalizeCard(card: WallCard): WallCard {
  return {
    ...card,
    assignedTo: card.assignedTo ?? '',
    cards: card.cards ?? [],
    children: card.children ?? [],
    collapsed: card.collapsed ?? false,
    color: card.color ?? '#ffff99',
    content: card.content ?? '',
    isLocked: card.isLocked ?? false,
    persistCluster: card.persistCluster ?? false,
    shape: card.shape ?? 'card',
    timeSpan: card.timeSpan ?? -1,
    timeSpanUnit: card.timeSpanUnit ?? '',
    title: card.title ?? '',
    type: card.type ?? 'text',
  };
}

function normalizeCardTree(card: WallCard): WallCard {
  const normalizedCard = normalizeCard(card);

  return {
    ...normalizedCard,
    cards: normalizedCard.cards.map((childCard) => normalizeCardTree(childCard)),
  };
}

function normalizeShape(shape: WallShape): WallShape {
  return {
    ...shape,
    attributes: shape.attributes ?? {},
    isLocked: shape.isLocked ?? false,
    type: shape.type ?? 'rectangle',
  };
}

function normalizeConnection(connection: WallConnection): WallConnection {
  return {
    ...connection,
    color: connection.color ?? '#000000',
    start: {
      cardId: connection.start?.cardId,
    },
    end: {
      cardId: connection.end?.cardId,
    },
  };
}
